/**
 * Reading Session Logger Service
 * שירות ללוג session של קריאה
 */

import { getDeviceInfo, getDeviceInfoString, DeviceInfo } from './device-info.service';
import { saveReadingSessionLog } from '../api/reading-session-log.service';

export interface ReadingSessionLogData {
  // Session timing
  sessionStart: Date;
  sessionEnd?: Date;
  durationMs?: number;
  
  // Reading statistics
  totalWords: number;
  correctWords: number;
  errorWords: number;
  accuracyPercentage: number;
  
  // Device info
  deviceInfo: DeviceInfo;
  
  // Session status
  status: 'completed' | 'abandoned' | 'crashed';
  completionReason: 'user_finished' | 'user_left' | 'system_error' | 'timeout';
  
  // Optional context
  bookId?: number;
  textEntityId?: number;
}

class ReadingSessionLogger {
  private sessionStartTime: Date | null = null;
  private deviceInfo: DeviceInfo | null = null;

  /**
   * Initialize a new reading session
   */
  startSession(): void {
    this.sessionStartTime = new Date();
    this.deviceInfo = getDeviceInfo();
    
    // Log to console (development)
    if (process.env.NODE_ENV === 'development') {
      console.log('📊 Reading Session Started', {
        startTime: this.sessionStartTime.toISOString(),
        device: getDeviceInfoString(),
      });
    }
  }

  /**
   * End reading session and log data
   */
  async endSession(
    stats: {
      totalWords: number;
      correctWords: number;
      errorWords: number;
      accuracyPercentage: number;
    },
    status: 'completed' | 'abandoned' | 'crashed' = 'completed',
    completionReason: 'user_finished' | 'user_left' | 'system_error' | 'timeout' = 'user_finished',
    context?: {
      bookId?: number;
      textEntityId?: number;
    }
  ): Promise<void> {
    if (!this.sessionStartTime || !this.deviceInfo) {
      console.warn('⚠️ Cannot end session - session was not started');
      return;
    }

    // חשוב: לתפוס את נתוני הסשן ולאפס אותו באופן *סינכרוני* לפני הקריאה האסינכרונית
    // לרשת. כך isSessionActive() יחזיר false מיד, וקריאת endSession שנייה ממסלול
    // סיום מקביל (stopListening / clearData / מעבר טאב) תיחסם — ולא תיווצר רשומה כפולה.
    const sessionStart = this.sessionStartTime;
    const deviceInfo = this.deviceInfo;
    this.sessionStartTime = null;
    this.deviceInfo = null;

    const sessionEnd = new Date();
    const durationMs = sessionEnd.getTime() - sessionStart.getTime();

    const logData: ReadingSessionLogData = {
      sessionStart,
      sessionEnd,
      durationMs,
      totalWords: stats.totalWords,
      correctWords: stats.correctWords,
      errorWords: stats.errorWords,
      accuracyPercentage: stats.accuracyPercentage,
      deviceInfo,
      status,
      completionReason,
      bookId: context?.bookId,
      textEntityId: context?.textEntityId,
    };

    // Log to console (development)
    if (process.env.NODE_ENV === 'development') {
      console.log('📊 Reading Session Ended', {
        duration: `${Math.round(durationMs / 1000)}s`,
        words: `${stats.correctWords}/${stats.totalWords}`,
        errors: stats.errorWords,
        accuracy: `${stats.accuracyPercentage}%`,
        status,
        reason: completionReason,
      });
    }

    // Send to server (async - don't block UI).
    // הסשן כבר אופס באופן סינכרוני למעלה, כך שכשל שמירה לא ישאיר סשן "תקוע".
    try {
      await saveReadingSessionLog(logData);
    } catch (error) {
      console.error('❌ Failed to save reading session log:', error);
      // Don't throw - logging failure shouldn't break the app
    }
  }

  /**
   * Get current session duration (if session is active)
   */
  getCurrentDuration(): number | null {
    if (!this.sessionStartTime) return null;
    return Date.now() - this.sessionStartTime.getTime();
  }

  /**
   * Check if session is active
   */
  isSessionActive(): boolean {
    return this.sessionStartTime !== null;
  }
}

// Singleton instance
export const readingSessionLogger = new ReadingSessionLogger();

